import React from "react";
import { Link } from "wouter";
import { LeadForm } from "@/components/PageComponents";
import Layout from "@/components/Layout";
import {
  MapPin, Phone, CheckCircle, ArrowRight, TrendingUp,
  Search, BarChart3, Globe, FileText, Star, Shield,
  Building2, Users, Layers, Award, Zap
} from "lucide-react";
import { SITE_PHONE } from "@/lib/siteData";
import Breadcrumb from "@/components/Breadcrumb";
import StateCityLinks from "@/components/StateCityLinks";

const pageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LocalBusiness",
      "url": "https://locranking-7q2qajau.manus.space/locations/iowa",
      "name": "Local Ranking Coach - Iowa SEO Agency",
      "description": "Local Ranking Coach provides expert local SEO services across Iowa, helping businesses in Des Moines, Cedar Rapids, Davenport, Iowa City, and Sioux City achieve top Google rankings and generate more leads.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Des Moines",
        "addressRegion": "IA",
        "addressCountry": "US"
      },
      "telephone": "+18003016220",
      "priceRange": "$$$",
      "image": "https://locranking-7q2qajau.manus.space/images/logo.png",
      "hasMap": "https://www.google.com/maps/search/Local+Ranking+Coach+Iowa",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
          "opens": "08:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Saturday"],
          "opens": "09:00",
          "closes": "14:00"
        }
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "127"
      },
      "areaServed": [
        {"@type": "City", "name": "Des Moines"},
        {"@type": "City", "name": "Cedar Rapids"},
        {"@type": "City", "name": "Davenport"},
        {"@type": "City", "name": "Iowa City"},
        {"@type": "City", "name": "Sioux City"}
      ],
      "sameAs": [
        "https://www.facebook.com/localrankingcoach",
        "https://twitter.com/localrankingcoach",
        "https://www.linkedin.com/company/localrankingcoach"
      ]
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Why is local SEO important for Iowa businesses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Iowa businesses rely on local SEO to capture the 46% of all Google searches with local intent. Whether you serve Des Moines professionals, Cedar Rapids manufacturers, or Iowa City students, appearing in the Google Local Pack drives phone calls, website visits, and in-store traffic. Iowa\'s diverse economy — spanning agriculture, insurance, healthcare, and manufacturing — means competitors are investing in local search. Without a strong local SEO strategy, you are invisible to the customers actively searching for your services right now."
          }
        },
        {
          "@type": "Question",
          "name": "How long does it take to see SEO results in Iowa?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most Iowa businesses see measurable improvements in Google Maps visibility and organic rankings within 60 to 90 days of starting a local SEO campaign. Competitive markets like Des Moines and Cedar Rapids may take 3 to 6 months to achieve top-3 Local Pack positions. We track keyword rankings, Maps impressions, and lead volume monthly so you always know exactly where you stand and what is driving growth."
          }
        },
        {
          "@type": "Question",
          "name": "Do you serve small businesses across rural Iowa?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. We serve businesses of all sizes across Iowa — from solo contractors in Ames and Waterloo to multi-location enterprises in the Quad Cities. Our rural Iowa SEO strategies focus on dominating county-level and regional searches where competition is lower but purchase intent is extremely high. A plumber in Dubuque or a dentist in Ankeny can achieve top Google rankings faster than their Des Moines counterparts because the competitive landscape is less saturated."
          }
        },
        {
          "@type": "Question",
          "name": "What industries do you serve in Iowa?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We specialize in local SEO for Iowa\'s core industries: HVAC and plumbing contractors, roofing and general contractors, healthcare and dental practices, legal and professional services, insurance agencies, restaurants and food service, agricultural equipment dealers, and retail businesses. Each industry requires a different keyword strategy, citation profile, and content approach — and our Iowa SEO team has deep experience across all of them."
          }
        },
        {
          "@type": "Question",
          "name": "What is included in your Iowa SEO service plans?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Every Iowa SEO plan includes a comprehensive local market audit, Google Business Profile optimization, citation building and cleanup, on-page SEO for your key service pages, monthly content creation, review generation strategy, and detailed monthly reporting. Growth and Authority plans add link acquisition, competitor gap analysis, and dedicated account management. All plans are month-to-month with no long-term contracts."
          }
        },
        {
          "@type": "Question",
          "name": "How do you measure SEO success for Iowa businesses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We track the metrics that actually drive revenue: Google Maps Local Pack rankings for your target keywords, organic search traffic from Iowa-based searchers, Google Business Profile impressions and call clicks, website conversion rates, and total lead volume. Every month you receive a plain-English performance report that connects SEO activity directly to business outcomes — not vanity metrics like domain authority or raw traffic numbers."
          }
        }
      ]
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://locranking-7q2qajau.manus.space"},
        {"@type": "ListItem", "position": 2, "name": "Locations", "item": "https://locranking-7q2qajau.manus.space/locations"},
        {"@type": "ListItem", "position": 3, "name": "Iowa", "item": "https://locranking-7q2qajau.manus.space/locations/iowa"}
      ]
    },
    {
      "@type": "WebPage",
      "url": "https://locranking-7q2qajau.manus.space/locations/iowa",
      "name": "Iowa SEO Agency | Local Ranking Coach",
      "description": "Expert local SEO services for Iowa businesses. We help Des Moines, Cedar Rapids, Davenport, Iowa City, and Sioux City businesses dominate Google search and generate more leads.",
      "breadcrumb": {"@type": "BreadcrumbList", "itemListElement": [
        {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://locranking-7q2qajau.manus.space"},
        {"@type": "ListItem", "position": 2, "name": "Locations", "item": "https://locranking-7q2qajau.manus.space/locations"},
        {"@type": "ListItem", "position": 3, "name": "Iowa", "item": "https://locranking-7q2qajau.manus.space/locations/iowa"}
      ]}
    }
  ]
};

const IA_CITIES = [
  {
    name: "Des Moines",
    slug: "des-moines-ia-seo",
    pop: "215,000+",
    tag: "State Capital & Insurance Hub",
    desc: "Iowa\'s largest city and financial capital. Dominant insurance, healthcare, and government sectors drive high-value local search demand."
  },
  {
    name: "Cedar Rapids",
    slug: "cedar-rapids-ia-seo",
    pop: "137,000+",
    tag: "Manufacturing & Tech Corridor",
    desc: "Iowa\'s second-largest city with a strong manufacturing base, growing tech sector, and a competitive local business landscape."
  },
  {
    name: "Davenport",
    slug: "davenport-ia-seo",
    pop: "101,000+",
    tag: "Quad Cities Gateway",
    desc: "Anchor of the Quad Cities metro, Davenport spans the Iowa-Illinois border with a diverse economy and cross-state search market."
  },
  {
    name: "Iowa City",
    slug: "iowa-city-ia-seo",
    pop: "74,000+",
    tag: "University & Healthcare Market",
    desc: "Home to the University of Iowa and a major medical center, Iowa City offers unique SEO opportunities in education, healthcare, and student services."
  },
  {
    name: "Sioux City",
    slug: "sioux-city-ia-seo",
    pop: "85,000+",
    tag: "Tri-State Agricultural Hub",
    desc: "Sioux City sits at the Iowa-Nebraska-South Dakota border, serving a large regional market with strong agricultural, food processing, and healthcare sectors."
  }
];

const IA_NEIGHBORHOODS = [
  "East Village", "Beaverdale", "Ingersoll Park", "Sherman Hill", "Drake",
  "New Hope", "Waveland Park", "Merle Hay", "Ankeny", "West Des Moines",
  "Urbandale", "Johnston", "Clive", "Waukee", "Altoona",
  "Marion", "Hiawatha", "Robins", "Ely", "Mount Vernon",
  "Coralville", "North Liberty", "Tiffin", "Hills", "Solon",
  "Bettendorf", "Moline", "Rock Island", "East Moline", "Milan",
  "South Sioux City", "Dakota City", "North Sioux City", "Sergeant Bluff", "Morningside",
  "Ames", "Waterloo", "Dubuque", "Council Bluffs", "Ottumwa"
];

const IA_PLANS = [
  {
    name: "Starter",
    price: "$997",
    period: "/mo",
    highlight: false,
    tagline: "Perfect for single-location Iowa businesses",
    features: [
      "Iowa market keyword audit",
      "Google Business Profile optimization",
      "20 local citation builds",
      "On-page SEO (up to 5 pages)",
      "Monthly performance report",
      "Review generation strategy",
      "Email support"
    ],
    cta: "Start Growing"
  },
  {
    name: "Growth",
    price: "$1,997",
    period: "/mo",
    highlight: true,
    tagline: "Most popular for competitive Iowa markets",
    features: [
      "Everything in Starter",
      "Competitor gap analysis",
      "4 SEO blog posts/month",
      "Link acquisition (5/mo)",
      "Google Maps rank tracking",
      "Bi-weekly strategy calls",
      "Priority support"
    ],
    cta: "Get More Leads"
  },
  {
    name: "Authority",
    price: "$3,497",
    period: "/mo",
    highlight: false,
    tagline: "For multi-location or dominant market position",
    features: [
      "Everything in Growth",
      "Multi-location SEO management",
      "8 SEO blog posts/month",
      "Premium link acquisition (10/mo)",
      "Conversion rate optimization",
      "Dedicated account manager",
      "Weekly reporting & calls"
    ],
    cta: "Dominate Iowa"
  }
];

const IA_FAQS = [
  {
    q: "Why is local SEO important for Iowa businesses?",
    a: "Iowa businesses rely on local SEO to capture the 46% of all Google searches with local intent. Whether you serve Des Moines professionals, Cedar Rapids manufacturers, or Iowa City students, appearing in the Google Local Pack drives phone calls, website visits, and in-store traffic. Iowa\'s diverse economy — spanning agriculture, insurance, healthcare, and manufacturing — means competitors are investing in local search. Without a strong local SEO strategy, you are invisible to the customers actively searching for your services right now."
  },
  {
    q: "How long does it take to see SEO results in Iowa?",
    a: "Most Iowa businesses see measurable improvements in Google Maps visibility and organic rankings within 60 to 90 days. Competitive markets like Des Moines and Cedar Rapids may take 3 to 6 months to achieve top-3 Local Pack positions. We track keyword rankings, Maps impressions, and lead volume monthly so you always know exactly where you stand."
  },
  {
    q: "Do you serve small businesses across rural Iowa?",
    a: "Yes. We serve businesses of all sizes across Iowa — from solo contractors in Ames and Waterloo to multi-location enterprises in the Quad Cities. Our rural Iowa SEO strategies focus on dominating county-level and regional searches where competition is lower but purchase intent is extremely high."
  },
  {
    q: "What industries do you serve in Iowa?",
    a: "We specialize in local SEO for Iowa\'s core industries: HVAC and plumbing contractors, roofing and general contractors, healthcare and dental practices, legal and professional services, insurance agencies, restaurants and food service, agricultural equipment dealers, and retail businesses."
  },
  {
    q: "What is included in your Iowa SEO service plans?",
    a: "Every Iowa SEO plan includes a comprehensive local market audit, Google Business Profile optimization, citation building and cleanup, on-page SEO for your key service pages, monthly content creation, review generation strategy, and detailed monthly reporting. Growth and Authority plans add link acquisition, competitor gap analysis, and dedicated account management."
  },
  {
    q: "How do you measure SEO success for Iowa businesses?",
    a: "We track the metrics that actually drive revenue: Google Maps Local Pack rankings, organic search traffic from Iowa-based searchers, Google Business Profile impressions and call clicks, website conversion rates, and total lead volume. Every month you receive a plain-English performance report connecting SEO activity directly to business outcomes."
  }
];

export default function IowaPage() {
  const hreflangLinks = [
    { rel: "alternate", hreflang: "en-US", href: "https://locranking-7q2qajau.manus.space/locations/iowa" },
    { rel: "alternate", hreflang: "es", href: "https://locranking-7q2qajau.manus.space/es/locations/iowa" },
    { rel: "alternate", hreflang: "x-default", href: "https://locranking-7q2qajau.manus.space/locations/iowa" }
  ];

  return (
    <Layout
      title="Iowa SEO Agency | Local Ranking Coach — Des Moines, Cedar Rapids & Beyond"
      description="Expert local SEO services for Iowa businesses. We help Des Moines, Cedar Rapids, Davenport, Iowa City, and Sioux City businesses dominate Google search and generate more leads."
      schema={pageSchema}
      hreflangLinks={hreflangLinks}
    >
      <Breadcrumb items={[
        { label: "Home", href: "/" },
        { label: "Locations", href: "/locations" },
        { label: "Iowa", href: "/locations/iowa" }
      ]} />

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="bg-navy pt-32 pb-16 md:pt-36 md:pb-20" aria-labelledby="iowa-hero-heading">
        <div className="container max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 rounded-full px-4 py-1.5 mb-6">
                <Star className="w-4 h-4 text-gold" aria-hidden="true" />
                <span className="text-gold text-sm font-semibold">Trusted by 500+ Iowa Businesses</span>
              </div>
              <h1 id="iowa-hero-heading" className="text-4xl lg:text-5xl font-black text-white leading-tight mb-6">
                Iowa SEO Agency That <span className="text-gold">Delivers Real Results</span>
              </h1>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                Local Ranking Coach is Iowa's premier local SEO agency. We help businesses in Des Moines, Cedar Rapids, Davenport, Iowa City, and Sioux City dominate Google Maps, rank higher in organic search, and generate a consistent flow of qualified leads. With 20+ years of proven expertise, we understand the unique dynamics of Iowa's diverse markets — from the insurance corridors of Des Moines to the agricultural hubs of Sioux City.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <a
                  href={`tel:${SITE_PHONE}`}
                  className="inline-flex items-center gap-2 bg-gold text-navy font-bold px-6 py-3 rounded-xl hover:bg-gold/90 transition-all active:scale-[0.97]"
                  aria-label="Call Local Ranking Coach"
                >
                  <Phone className="w-5 h-5" aria-hidden="true" /> Call Now
                </a>
                <a
                  href="#ia-lead-form"
                  className="inline-flex items-center gap-2 bg-white/10 text-white font-bold px-6 py-3 rounded-xl hover:bg-white/20 transition-all active:scale-[0.97]"
                >
                  <ArrowRight className="w-5 h-5" aria-hidden="true" /> Get Free Audit
                </a>
              </div>
              <div className="flex flex-wrap gap-6 text-sm text-white/60">
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-gold" aria-hidden="true" /> No Long-Term Contracts</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-gold" aria-hidden="true" /> Results in 60–90 Days</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-gold" aria-hidden="true" /> Iowa-Focused Strategies</span>
              </div>
            </div>
            <div id="ia-lead-form">
              <LeadForm
                cityDefault="Iowa"
                title="Get Your Free Iowa SEO Audit"
                subtitle="Discover what's holding your Iowa rankings back — no obligation."
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ────────────────────────────────────────────────────────── */}
      <section className="bg-gold py-10" aria-label="Iowa SEO performance statistics">
        <div className="container max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: "20+", label: "Years of Experience" },
              { value: "500+", label: "Iowa Clients Served" },
              { value: "95%", label: "Client Retention Rate" },
              { value: "3×", label: "Average Lead Increase" }
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl md:text-4xl font-black text-navy mb-1">{stat.value}</div>
                <div className="text-navy/70 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY IOWA ─────────────────────────────────────────────────────────── */}
      <section className="section-padding bg-white" aria-labelledby="why-iowa-heading">
        <div className="container max-w-7xl">
          <div className="text-center mb-12">
            <h2 id="why-iowa-heading" className="heading-lg text-navy mb-4">Why Iowa Businesses Choose Local Ranking Coach</h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-4" aria-hidden="true" />
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Iowa's economy spans agriculture, insurance, healthcare, manufacturing, and education. Each sector demands a different local SEO approach — and we have the proven playbook for all of them.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MapPin, title: "Iowa Market Expertise", desc: "We understand the unique search behaviors of Iowa consumers — from Des Moines metro professionals to rural county-seat business owners. Our strategies are built on real Iowa market data, not generic templates." },
              { icon: BarChart3, title: "Proven Local Pack Dominance", desc: "Our clients consistently rank in the top 3 of Google's Local Pack for their target keywords. We combine Google Business Profile optimization, citation authority, and review velocity to lock in top positions." },
              { icon: Search, title: "Semantic SEO Content", desc: "We write Iowa-specific content that satisfies Google's E-E-A-T signals — demonstrating Experience, Expertise, Authoritativeness, and Trustworthiness for every market we serve across the Hawkeye State." },
              { icon: Globe, title: "Multi-City Iowa Coverage", desc: "From the Quad Cities to the Missouri River corridor, we manage local SEO campaigns across all of Iowa's major markets simultaneously, ensuring consistent brand visibility statewide." },
              { icon: Shield, title: "Transparent Reporting", desc: "Every month you receive a plain-English performance report that connects SEO activity to business outcomes — keyword rankings, Maps impressions, call clicks, and lead volume — no vanity metrics." },
              { icon: TrendingUp, title: "No Long-Term Contracts", desc: "We earn your business every month. Our Iowa SEO clients stay because they see results — not because they are locked into a contract. Month-to-month agreements with a 30-day cancellation policy." }
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl border border-gray-100 hover:border-gold/30 transition-all">
                <div className="w-10 h-10 rounded-lg bg-navy/5 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-navy" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-bold text-navy mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── IOWA CITIES ──────────────────────────────────────────────────────── */}
      <section id="ia-cities" className="section-padding bg-gray-50" aria-labelledby="ia-cities-heading">
        <div className="container max-w-7xl">
          <div className="text-center mb-12">
            <h2 id="ia-cities-heading" className="heading-lg text-navy mb-4">Iowa Cities We Serve</h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-4" aria-hidden="true" />
            <p className="text-gray-600 max-w-2xl mx-auto">
              From Iowa's capital city to its agricultural hubs, we deliver city-specific SEO strategies that account for local competition, search volume, and consumer behavior in each market.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {IA_CITIES.map((city) => (
              <Link
                key={city.slug}
                href={`/locations/iowa/${city.slug}`}
                className="group block bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:border-gold/40 hover:shadow-md transition-all"
                aria-label={`SEO services in ${city.name}, Iowa`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-navy text-lg group-hover:text-gold transition-colors">{city.name}</h3>
                    <span className="text-xs text-gold font-semibold uppercase tracking-wide">{city.tag}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400 font-medium">Population</div>
                    <div className="text-sm font-bold text-navy">{city.pop}</div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{city.desc}</p>
                <div className="flex items-center gap-1 text-gold text-sm font-semibold">
                  View Iowa City SEO <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3-STEP PROCESS ───────────────────────────────────────────────────── */}
      <section className="section-padding bg-navy" aria-labelledby="ia-process-heading">
        <div className="container max-w-7xl">
          <div className="text-center mb-12">
            <h2 id="ia-process-heading" className="heading-lg text-white mb-4">Our Iowa SEO Process</h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-4" aria-hidden="true" />
            <p className="text-white/70 max-w-2xl mx-auto">
              A proven 3-step framework that takes Iowa businesses from invisible to dominant in Google Maps and organic search.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", icon: Search, title: "Iowa Market Audit", desc: "We analyze your current rankings, competitor strategies, Google Business Profile health, citation consistency, and the specific search landscape for your industry across Iowa's key markets. This audit forms the foundation of your custom Iowa SEO roadmap." },
              { step: "02", icon: Layers, title: "Iowa SEO Strategy", desc: "We build a city-specific SEO strategy that targets the exact keywords your Iowa customers are searching. This includes on-page optimization, Google Maps strategy, content calendar, citation building plan, and link acquisition targets tailored to your Iowa market." },
              { step: "03", icon: TrendingUp, title: "Execute & Scale", desc: "Our Iowa SEO team implements every element of the strategy simultaneously — on-page optimization, citation building, content creation, link acquisition, and Google Business Profile management — then scales what works based on monthly performance data." }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-gold" aria-hidden="true" />
                </div>
                <div className="text-gold font-extrabold text-sm mb-2 tracking-widest">{item.step}</div>
                <h3 className="font-bold text-white text-lg mb-3">{item.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INDUSTRIES ───────────────────────────────────────────────────────── */}
      <section className="section-padding bg-white" aria-labelledby="ia-industries-heading">
        <div className="container max-w-7xl">
          <div className="text-center mb-12">
            <h2 id="ia-industries-heading" className="heading-lg text-navy mb-4">Industries We Serve in Iowa</h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-4" aria-hidden="true" />
            <p className="text-gray-600 max-w-2xl mx-auto">
              Iowa's economy is as diverse as its landscape. We deliver industry-specific SEO strategies built around the unique search behaviors and competitive dynamics of each sector.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Zap, title: "HVAC & Plumbing", desc: "Iowa's extreme winters and hot summers create year-round demand for HVAC and plumbing services. We rank Iowa contractors for emergency service keywords and seasonal search surges across Des Moines, Cedar Rapids, and beyond." },
              { icon: Building2, title: "Roofing & Contractors", desc: "Iowa's storm season and ongoing residential construction create strong demand for roofing and general contracting services. We position Iowa contractors for both emergency and planned project searches." },
              { icon: Shield, title: "Healthcare & Dental", desc: "Iowa's growing population needs local healthcare providers. We help medical practices, dental offices, and specialists rank for high-intent patient searches across all Iowa markets." },
              { icon: Users, title: "Legal & Professional Services", desc: "From insurance attorneys in Des Moines to financial advisors in Cedar Rapids, we help Iowa professional service firms build authoritative local search presences that generate consistent client inquiries." },
              { icon: Star, title: "Restaurants & Food Service", desc: "Iowa's vibrant food scene competes for Google Maps visibility. We help restaurants, catering companies, and food service businesses rank for high-intent dining and catering searches statewide." },
              { icon: Award, title: "Agricultural & Equipment", desc: "Iowa's agricultural backbone creates unique SEO opportunities for equipment dealers, ag suppliers, and rural service businesses. We understand the seasonal search patterns and geographic targeting needed to reach Iowa's farming community." }
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100 shadow-sm hover:border-gold/30 transition-all">
                <div className="w-10 h-10 rounded-lg bg-navy/5 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-navy" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-bold text-navy mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICE PLANS ────────────────────────────────────────────────────── */}
      <section id="ia-service-plans" className="section-padding bg-gray-950" aria-labelledby="ia-plans-heading">
        <div className="container max-w-7xl">
          <div className="text-center mb-14">
            <span className="inline-block bg-gold/10 text-gold text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Transparent Pricing</span>
            <h2 id="ia-plans-heading" className="text-3xl md:text-4xl font-black text-white mb-4">Iowa SEO Service Plans</h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Every plan includes a dedicated Iowa SEO strategist, monthly performance reporting, and a no-contract guarantee. Choose the investment level that matches your growth goals.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 items-start">
            {IA_PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl p-8 flex flex-col ${plan.highlight ? "bg-gold text-navy ring-4 ring-gold/50 scale-105" : "bg-navy text-white"}`}
              >
                {plan.highlight && (
                  <div className="text-center mb-4">
                    <span className="inline-block bg-navy text-gold text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">Most Popular</span>
                  </div>
                )}
                <h3 className="text-2xl font-black mb-1">{plan.name}</h3>
                <p className={`text-sm mb-4 ${plan.highlight ? "text-navy/70" : "text-white/60"}`}>{plan.tagline}</p>
                <div className="flex items-end gap-1 mb-6">
                  <span className="text-4xl font-black">{plan.price}</span>
                  <span className={`text-sm pb-1 ${plan.highlight ? "text-navy/60" : "text-white/50"}`}>{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.highlight ? "text-navy" : "text-gold"}`} aria-hidden="true" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#ia-lead-form"
                  className={`block text-center font-bold py-3 rounded-xl transition-all active:scale-[0.97] ${plan.highlight ? "bg-navy text-white hover:bg-navy/90" : "bg-gold text-navy hover:bg-gold/90"}`}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEIGHBORHOODS ────────────────────────────────────────────────────── */}
      <section className="section-padding bg-white" aria-labelledby="ia-neighborhoods-heading">
        <div className="container max-w-7xl">
          <div className="text-center mb-10">
            <h2 id="ia-neighborhoods-heading" className="heading-lg text-navy mb-4">Iowa Communities We Serve</h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-4" aria-hidden="true" />
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our Iowa SEO strategies target the specific neighborhoods, suburbs, and communities where your customers live and search — from Des Moines metro suburbs to Quad Cities communities.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {IA_NEIGHBORHOODS.map((n) => (
              <div key={n} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100 hover:border-gold/30 transition-all">
                <MapPin className="w-3.5 h-3.5 text-gold flex-shrink-0" aria-hidden="true" />
                <span className="text-sm text-navy font-medium">{n}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────────── */}
      <section className="section-padding bg-gray-50" aria-labelledby="ia-faq-heading">
        <div className="container max-w-4xl">
          <div className="text-center mb-10">
            <h2 id="ia-faq-heading" className="heading-lg text-navy mb-4">Iowa SEO Frequently Asked Questions</h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full" aria-hidden="true" />
          </div>
          <div className="space-y-4">
            {IA_FAQS.map((faq, i) => (
              <details key={i} className="group bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-navy list-none">
                  <span>{faq.q}</span>
                  <span className="ml-4 text-gold group-open:rotate-45 transition-transform text-xl leading-none" aria-hidden="true">+</span>
                </summary>
                <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── OTHER STATES ─────────────────────────────────────────────────────── */}
      <section className="section-padding bg-white" aria-labelledby="ia-states-heading">
        <div className="container max-w-7xl">
          <div className="text-center mb-8">
            <h2 id="ia-states-heading" className="text-xl font-bold text-navy mb-2">We Also Serve Neighboring States</h2>
            <p className="text-gray-500 text-sm">Local SEO expertise across the Midwest and beyond</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Illinois", slug: "illinois" },
              { name: "Minnesota", slug: "minnesota" },
              { name: "Wisconsin", slug: "wisconsin" },
              { name: "Missouri", slug: "missouri" },
              { name: "Nebraska", slug: "nebraska" },
              { name: "South Dakota", slug: "south-dakota" }
            ].map((state) => (
              <Link
                key={state.slug}
                href={`/locations/${state.slug}`}
                className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 text-navy text-sm font-medium px-4 py-2 rounded-lg hover:border-gold/40 hover:bg-gold/5 transition-all"
              >
                <MapPin className="w-3.5 h-3.5 text-gold" aria-hidden="true" /> {state.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ───────────────────────────────────────────────────────── */}
      <section className="py-20 bg-navy text-white" aria-labelledby="ia-cta-heading">
        <div className="container max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 id="ia-cta-heading" className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Ready to Dominate Iowa Search Results?
              </h2>
              <p className="text-white/70 leading-relaxed mb-6">
                Join hundreds of Iowa businesses that trust Local Ranking Coach to deliver measurable local SEO results. Get your free Iowa SEO audit today — no obligation, no contracts, just a clear roadmap to more leads from Google.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <CheckCircle className="w-4 h-4 text-gold" aria-hidden="true" /> Free Iowa SEO Audit
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <CheckCircle className="w-4 h-4 text-gold" aria-hidden="true" /> No Long-Term Contracts
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <CheckCircle className="w-4 h-4 text-gold" aria-hidden="true" /> Results in 60-90 Days
                </div>
              </div>
            </div>
            <div>
              <LeadForm
                cityDefault="Iowa"
                title="Start Your Free Iowa SEO Audit"
                subtitle="Discover what's holding your Iowa rankings back — no obligation."
              />
            </div>
          </div>
        </div>
      </section>
      <StateCityLinks stateSlug="iowa" stateName="Iowa" />
    </Layout>
  );
}
